import "styles/modal.scss";
import "./UploadMetadataGate.scss";

import cx from "classnames";
import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import { Checkbox } from "components/inputs";
import type { SelectHTMLAttributes } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useConstants } from "utils/hooks";

import {
  LINKABLE_SERVICE_TYPES,
  MSA_SERVICE_TYPES,
  parseLienOptionValue,
  renderServiceOptions,
} from "../shared/ServiceSelector";
import type { FileService } from "../shared/ServiceSelector";

const NativeSelect = ({
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) => (
  <div className="upload-metadata-gate__select">
    <select {...props}>{children}</select>
    <span className="material-icons">keyboard_arrow_down</span>
  </div>
);

export type UploadMetadataFileResult = {
  file: File;
  documentType?: string;
  arScreeningDocumentType?: string;
  serviceFields: Record<string, string>;
};

export type UploadMetadataResult = UploadMetadataFileResult[];

type FileRowState = {
  documentType: string;
  arScreeningDocumentType: string;
  serviceKey: string;
};

// rows and applyToAll live in one state object so functional updaters
// always see the current applyToAll, never a stale render closure
type GateState = {
  rows: FileRowState[];
  applyToAll: boolean;
};

const APPLY_ALL_DESCRIPTION_ID = "upload-metadata-apply-all-description";

type UploadMetadataGateProps = {
  isOpen: boolean;
  files: File[];
  serviceType?: string;
  fileServices: FileService[];
  onCancel: () => void;
  onConfirm: (results: UploadMetadataResult) => void;
};

const emptyRow = (): FileRowState => ({
  documentType: "",
  arScreeningDocumentType: "",
  serviceKey: "",
});

// Exported for direct unit testing
export const buildServiceFields = (
  service: FileService,
): Record<string, string> => {
  if (MSA_SERVICE_TYPES.includes(service.type) && service.msa != null) {
    return { msa_id: String(service.msa) };
  }
  if (
    (service.type === "LCP" || service.type === "MCP") &&
    service.costprojection != null
  ) {
    return { cost_projection_id: String(service.costprojection) };
  }
  if (service.type === "ARS" && service.amended_review_screening != null) {
    return {
      amended_review_screening_id: String(service.amended_review_screening),
    };
  }
  if (service.type === "DOCS" && service.document_management_file != null) {
    return {
      document_management_file_id: String(service.document_management_file),
    };
  }
  if (service.type === "LIEN" && service.lien_file != null) {
    return { lien_file_id: String(service.lien_file) };
  }
  // No real record yet (unopened file); tag the pending service instead,
  // opening the file later backfills the real FK
  return { service_id: String(service.id) };
};

// Exported for direct unit testing
export const resolveServiceFields = (
  serviceKey: string,
  availableServices: FileService[],
): Record<string, string> => {
  const lienId = parseLienOptionValue(serviceKey);
  if (lienId != null) {
    // individual lien selected; tag the lien file plus the specific lien
    const service = availableServices.find(
      (s) => s.type === "LIEN" && s.liens?.some((l) => l.id === lienId),
    );
    if (!service) {
      // Stale selection (services changed since the pick); upload without a
      // service link rather than a wrong one, matching the drawer's no-op
      console.error("Lien service not found for selected lien:", lienId);
      return {};
    }
    const fields = buildServiceFields(service);
    // Only send lien_id alongside the real lien file FK, mirroring the
    // drawer's service-change path; a pending service gets service_id only
    if (fields.lien_file_id) {
      fields.lien_id = String(lienId);
    }
    return fields;
  }
  const service = availableServices.find((s) => String(s.id) === serviceKey);
  return service ? buildServiceFields(service) : {};
};

const UploadMetadataGate = ({
  isOpen,
  files,
  serviceType,
  fileServices,
  onCancel,
  onConfirm,
}: UploadMetadataGateProps) => {
  const { constants } = useConstants();
  const [{ rows, applyToAll }, setState] = useState<GateState>({
    rows: [],
    applyToAll: false,
  });

  // Rebuild rows on a new drop. Compares the File objects, not the array
  // reference, so a re-render with a rebuilt files array doesn't wipe
  // in-progress picks, while a new drop of the same number of files does
  const prevFilesRef = useRef<File[]>([]);
  useEffect(() => {
    if (!isOpen || files.length === 0) return;
    const prev = prevFilesRef.current;
    const isSameDrop =
      prev.length === files.length && prev.every((f, i) => f === files[i]);
    if (!isSameDrop) {
      prevFilesRef.current = files;
      setState({ rows: files.map(() => emptyRow()), applyToAll: false });
    }
  }, [isOpen, files]);

  const documentTypes = useMemo(
    () =>
      (constants?.DOCUMENT_TYPES || [])
        // Exclude Unknown so Miscellaneous is the escape hatch instead
        .filter((type) => type.value !== null && type.value !== "UNKNOWN")
        .slice()
        .sort((a, b) => (a.label || "").localeCompare(b.label || "")),
    [constants?.DOCUMENT_TYPES],
  );

  const arScreeningDocumentTypes = constants?.AR_SCREENING_DOCUMENT_TYPES || [];

  const isServiceFixed = Boolean(serviceType);
  // Callers use different casing elsewhere ("ars" vs "CON"/"AUD"), so
  // compare case-insensitively instead of assuming one
  const isArScreening = serviceType?.toLowerCase() === "ars";

  const fixedServiceLabel = useMemo(() => {
    if (!serviceType) return "";
    const upperServiceType = serviceType.toUpperCase();
    return (
      constants?.SERVICE_TYPE?.find((s) => s.value === upperServiceType)
        ?.label || upperServiceType
    );
  }, [serviceType, constants?.SERVICE_TYPE]);

  const availableServices = useMemo(
    () =>
      fileServices.filter((service) =>
        LINKABLE_SERVICE_TYPES.includes(service.type),
      ),
    [fileServices],
  );

  const updateRow = (index: number, patch: Partial<FileRowState>) => {
    setState((prev) => ({
      ...prev,
      rows: prev.rows.map((row, i) =>
        i === index || (prev.applyToAll && index === 0)
          ? { ...row, ...patch }
          : row,
      ),
    }));
  };

  const handleApplyToAllChange = (checked: boolean) => {
    setState((prev) => ({
      applyToAll: checked,
      // Snap every row to the first document's picks (rows are always populated
      // by the time the checkbox is rendered); unchecking later leaves the
      // copies in place but makes the rows editable again
      rows: checked ? prev.rows.map(() => ({ ...prev.rows[0] })) : prev.rows,
    }));
  };

  const resetState = () => {
    // Also forget the last drop so re-dropping the same File objects
    // after a cancel starts from a clean form
    prevFilesRef.current = [];
    setState({ rows: [], applyToAll: false });
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  const handleConfirm = () => {
    const results: UploadMetadataResult = files.map((file, index) => {
      // Upload stays disabled until rows and files are index-aligned
      const row = rows[index];
      return {
        file,
        documentType: row.documentType || undefined,
        arScreeningDocumentType: row.arScreeningDocumentType || undefined,
        serviceFields: isServiceFixed
          ? {}
          : resolveServiceFields(row.serviceKey, availableServices),
      };
    });

    onConfirm(results);
    resetState();
  };

  const isConfirmDisabled =
    files.length === 0 ||
    rows.length !== files.length ||
    rows.some(
      (row) =>
        !row.documentType ||
        (isArScreening && !row.arScreeningDocumentType) ||
        (!isServiceFixed && !row.serviceKey),
    );

  return (
    <div className={cx("overlay overlay-slidedown", { open: isOpen })}>
      <div className="grid-layout gl-1 modal-grid-wrapper">
        <div className="data-block no-hover document-upload-data upload-metadata-gate">
          <div className="data-block-header flex ai-c jc-sb">
            Upload Details
            <ButtonIcon kind="neutral" onClick={handleCancel}>
              close
            </ButtonIcon>
          </div>

          <p className="upload-metadata-gate__hint">
            Choose a document type
            {isServiceFixed ? "" : " and service"} for each document before
            uploading.
            {isServiceFixed && (
              <>
                {" "}
                Service: <strong>{fixedServiceLabel}</strong> (applies to all
                documents).
              </>
            )}
          </p>

          {files.length > 1 && (
            <div className="upload-metadata-gate__apply-all">
              <Checkbox
                name="upload-metadata-apply-to-all"
                label="Apply first document's selections to all documents"
                checked={applyToAll}
                onChange={(e) => handleApplyToAllChange(e.target.checked)}
              />
              <span
                id={APPLY_ALL_DESCRIPTION_ID}
                className="upload-metadata-gate__visually-hidden"
              >
                Controlled by Apply to all; edit the first document's row
                instead.
              </span>
            </div>
          )}

          <div className="upload-metadata-gate__table-wrap">
            <table className="upload-metadata-gate__table">
              <thead>
                <tr>
                  <th className="upload-metadata-gate__col-file">File</th>
                  <th className="upload-metadata-gate__col-type">
                    Document Type
                  </th>
                  {isArScreening && (
                    <th className="upload-metadata-gate__col-type">
                      AR Screening Type
                    </th>
                  )}
                  {!isServiceFixed && (
                    <th className="upload-metadata-gate__col-service">
                      Service Type
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {files.map((file, index) => {
                  // Unlike handleConfirm, this can run before the rebuild
                  // effect fires, so the fallback is needed here
                  const row = rows[index] || emptyRow();
                  // With Apply to all on, only the first row stays editable
                  const isRowLocked = applyToAll && index > 0;
                  const lockedDescription = isRowLocked
                    ? APPLY_ALL_DESCRIPTION_ID
                    : undefined;
                  // The files array is replaced atomically on each drop
                  // (never appended to), so the index is a stable key even
                  // for duplicate filenames
                  return (
                    <tr
                      key={index}
                      className={cx({
                        "upload-metadata-gate__row--locked": isRowLocked,
                      })}
                    >
                      <td className="upload-metadata-gate__col-file">
                        <span
                          className="upload-metadata-gate__filename"
                          title={file.name}
                        >
                          {file.name}
                        </span>
                      </td>
                      <td className="upload-metadata-gate__col-type">
                        <NativeSelect
                          value={row.documentType}
                          onChange={(e) =>
                            updateRow(index, {
                              documentType: e.target.value,
                            })
                          }
                          disabled={isRowLocked}
                          aria-label={`Document type for ${file.name}`}
                          aria-describedby={lockedDescription}
                        >
                          <option value="">Select document type</option>
                          {documentTypes.map((type) => (
                            <option key={type.value} value={type.value || ""}>
                              {type.label}
                            </option>
                          ))}
                        </NativeSelect>
                      </td>
                      {isArScreening && (
                        <td className="upload-metadata-gate__col-type">
                          <NativeSelect
                            value={row.arScreeningDocumentType}
                            onChange={(e) =>
                              updateRow(index, {
                                arScreeningDocumentType: e.target.value,
                              })
                            }
                            disabled={isRowLocked}
                            aria-label={`AR screening type for ${file.name}`}
                            aria-describedby={lockedDescription}
                          >
                            <option value="">Select AR screening type</option>
                            {arScreeningDocumentTypes
                              .filter((type) => type.value !== null)
                              .map((type) => (
                                <option
                                  key={type.value}
                                  value={type.value || ""}
                                >
                                  {type.label}
                                </option>
                              ))}
                          </NativeSelect>
                        </td>
                      )}
                      {!isServiceFixed && (
                        <td className="upload-metadata-gate__col-service">
                          <NativeSelect
                            value={row.serviceKey}
                            onChange={(e) =>
                              updateRow(index, {
                                serviceKey: e.target.value,
                              })
                            }
                            disabled={isRowLocked}
                            aria-label={`Service for ${file.name}`}
                            aria-describedby={lockedDescription}
                          >
                            <option value="">Select service</option>
                            {renderServiceOptions(availableServices)}
                          </NativeSelect>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex jc-c ai-c mt-1 w-100">
            <Button onClick={handleConfirm} disabled={isConfirmDisabled}>
              Upload {files.length > 1 ? `(${files.length} files)` : ""}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadMetadataGate;
